import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getViewsPerUserAndLesson } from '../../../server-functions/views';
import Table from '../../../global/components/YkTable';
import { exportToExcel } from '../../../utils/export-excel/excel';
import { printTable } from '../../../utils/print-pdf';
import { formatWatchTime } from "../../../global/functions";
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { viewsPerUserAndLessonTableColumns } from '../../../global/settings/tableColumns';
import dayjs from 'dayjs';
import 'dayjs/locale/he';

dayjs.locale('he');

function ViewsPerUserAndLesson({ userSelected, setUserSelected }) {

    const {
        data,
        status,
        refetch,
        error
    } = useQuery({
        queryKey: ['views-per-user-lesson'],
        queryFn: () => getViewsPerUserAndLesson(userSelected),
    });

    if (error) {
        console.error("Error fetching views per user and lesson:", error);
    }

    const processedData = React.useMemo(() => {
        return (data || []).map(row => ({
            ...row,
            totalWatchTimeFormatted: formatWatchTime(row.totalWatchTime),
            videoDurationFormatted: formatWatchTime(row.videoDuration),
        }));
    }, [data]);

    const tableTitle = React.useMemo(() => {
        if (!data || data.length === 0) return "דוח לפי משתמש ושיעור";
        return data[0]?.userName || "דוח לפי משתמש ושיעור";
    }, [data]);

    const actions = [
        {
            label: "איפוס משתמש",
            icon: <PersonOffIcon style={{ fontSize: 18 }} />,
            onClick: () => { setUserSelected(null); }
        },
        {
            label: "רענן נתונים",
            icon: <RefreshIcon style={{ fontSize: 18 }} />,
            onClick: refetch
        },
        {
            label: "הפקת אקסל",
            icon: <DownloadIcon style={{ fontSize: 18 }} />,
            onClick: (data) => {
                const mapped = (data || []).map(row => ({
                    'שם משתמש': row.userName,
                    'אימייל': row.userEmail,
                    'טלפון': row.userPhone,
                    'שיעור': row.lessonTitle,
                    'זמן צפייה': `${row.hasViews ? row.totalWatchTimeFormatted : '00:00'} / ${row.videoDurationFormatted}`,
                    'אחוז השלמה': `${row.watchPercentage}%`,
                    'מספר צפיות': row.sessionsCount,
                    'צפייה אחרונה': row.lastWatched
                        ? dayjs(row.lastWatched).format('DD/MM/YYYY HH:mm')
                        : 'לא צפה',
                }));
                exportToExcel(mapped, [], "דוח משתמש לפי שיעור");
            }
        },
        {
            label: "הדפס",
            icon: <LocalPrintshopIcon style={{ fontSize: 18 }} />,
            onClick: (data) => {
                const mapped = (data || []).map(row => ({
                    userName: row.userName,
                    userEmail: row.userEmail,
                    userPhone: row.userPhone,
                    lessonTitle: row.lessonTitle,
                    watchTime: `${row.hasViews ? row.totalWatchTimeFormatted : '00:00'} / ${row.videoDurationFormatted}`,
                    watchPercentage: `${row.watchPercentage}%`,
                    sessionsCount: row.sessionsCount,
                    lastWatched: row.lastWatched
                        ? dayjs(row.lastWatched).format('DD/MM/YYYY HH:mm')
                        : 'לא צפה',
                }));
                const printColumns = [
                    { key: 'userName', title: 'שם משתמש' },
                    { key: 'userEmail', title: 'אימייל' },
                    { key: 'userPhone', title: 'טלפון' },
                    { key: 'lessonTitle', title: 'שיעור' },
                    { key: 'watchTime', title: 'זמן צפייה' },
                    { key: 'videoDuration', title: 'אורך סרטון' },
                    { key: 'watchPercentage', title: 'אחוז השלמה' },
                    { key: 'sessionsCount', title: 'מספר צפיות' },
                    { key: 'lastWatched', title: 'צפייה אחרונה' },
                ];
                printTable(mapped, printColumns, "דוח משתמש לפי שיעור");
            }
        },
    ];

    return (
        <Table
            title={tableTitle}
            columns={viewsPerUserAndLessonTableColumns}
            data={processedData}
            loading={!userSelected || status === 'pending'}
            refetch={refetch}
            actions={actions}
        />
    );
}

export default ViewsPerUserAndLesson;
